import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * What came of asking the browser to install the app. `unavailable` means no native
 * prompt could be shown: the browser never offered one, it was already used, or
 * `prompt()` threw. Callers should fall back to manual steps rather than do nothing.
 */
export type PwaInstallOutcome = 'accepted' | 'dismissed' | 'unavailable'

// Capture at module load time — beforeinstallprompt fires once per page session, early in
// the load lifecycle, often before auth-gated components like GymLayout have mounted.
let _capturedPrompt: BeforeInstallPromptEvent | null = null
// Set only by `appinstalled`. A missing prompt is not proof of an install: the prompt is
// also gone once it has been shown and dismissed.
let _installed = false
const _subscribers = new Set<() => void>()
// How many prompts the browser has offered this page session. Zero when a tap finds no
// prompt means the browser never judged the app installable; more means it was spent.
let _promptsOffered = 0

export interface PwaInstallEvent {
  /** Short, stable name — this is what shows up in the issue list. */
  name: string
  /** Facts about the browser and the page. Nothing that identifies the member. */
  data: Record<string, unknown>
}

// This package must not depend on Sentry, so the sink is injected: each app wires
// `setPwaInstallTelemetry` in its entry point, the same way it wires `setAuthTelemetry`.
let _telemetry: ((event: PwaInstallEvent) => void) | null = null
// One report per name per page load. A member who taps a dead button five times is one
// problem, and every report counts against the Sentry quota.
const _reported = new Set<string>()

export function setPwaInstallTelemetry(sink: ((event: PwaInstallEvent) => void) | null) {
  _telemetry = sink
  _reported.clear()
}

/**
 * The things a browser checks before it offers an install prompt, as far as a page can
 * see them. A missing prompt with a controlling service worker and a manifest points at
 * the browser; one without them points at us.
 */
function installFacts(): Record<string, unknown> {
  try {
    return {
      promptsOffered: _promptsOffered,
      secureContext: window.isSecureContext,
      serviceWorker: !('serviceWorker' in navigator)
        ? 'unsupported'
        : navigator.serviceWorker.controller
          ? 'controlling'
          : 'not_controlling',
      manifestLink: !!document.querySelector('link[rel="manifest"]'),
      standalone: window.matchMedia('(display-mode: standalone)').matches,
      secondsSinceLoad: Math.round(performance.now() / 1000),
    }
  } catch {
    return { promptsOffered: _promptsOffered }
  }
}

function report(name: string, data: Record<string, unknown> = {}) {
  if (!_telemetry || _reported.has(name)) return
  _reported.add(name)
  try {
    _telemetry({ name, data: { ...installFacts(), ...data } })
  } catch {
    // Reporting a broken install must never be what breaks it.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    _capturedPrompt = e as BeforeInstallPromptEvent
    _installed = false
    _promptsOffered += 1
    _subscribers.forEach((fn) => fn())
  })
  window.addEventListener('appinstalled', () => {
    _capturedPrompt = null
    _installed = true
    _subscribers.forEach((fn) => fn())
  })
}

export function usePwaInstall(onInstalled?: () => void) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    _capturedPrompt,
  )
  const [isStandalone, setIsStandalone] = useState(false)
  const isIos = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent)
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)
  const onInstalledRef = useRef(onInstalled)
  useLayoutEffect(() => {
    onInstalledRef.current = onInstalled
  })

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)')
    const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true
    setIsStandalone(mq.matches || iosStandalone)

    const onDisplayChange = (e: MediaQueryListEvent) => {
      setIsStandalone(e.matches)
      if (e.matches) {
        _capturedPrompt = null
        setDeferredPrompt(null)
      }
    }
    mq.addEventListener('change', onDisplayChange)

    const onGlobalChange = () => {
      setDeferredPrompt(_capturedPrompt)
      if (_installed) {
        setIsStandalone(true)
        onInstalledRef.current?.()
      }
    }
    _subscribers.add(onGlobalChange)

    return () => {
      mq.removeEventListener('change', onDisplayChange)
      _subscribers.delete(onGlobalChange)
    }
  }, [])

  const install = useCallback(async (): Promise<PwaInstallOutcome> => {
    const prompt = _capturedPrompt
    if (!prompt) {
      report(
        _promptsOffered > 0
          ? 'pwa_install_prompt_already_used'
          : 'pwa_install_prompt_never_offered',
      )
      return 'unavailable'
    }
    // A captured prompt can be shown once; calling prompt() on it again rejects. Drop it
    // before awaiting so a second tap reports `unavailable` instead of silently failing,
    // even if the browser never settles this one.
    _capturedPrompt = null
    try {
      await prompt.prompt()
      const { outcome } = await prompt.userChoice
      return outcome
    } catch (err) {
      // Read by shape, not `instanceof Error`: a DOMException is not one in every runtime.
      const { name, message } = (err ?? {}) as { name?: unknown; message?: unknown }
      report('pwa_install_prompt_failed', {
        errorName: typeof name === 'string' ? name : typeof err,
        errorMessage: typeof message === 'string' ? message : String(err),
      })
      return 'unavailable'
    } finally {
      _subscribers.forEach((fn) => fn())
    }
  }, [])

  return {
    canInstall: !isStandalone && !!deferredPrompt,
    canShowIosHint: isIos && !isStandalone,
    hasNativePrompt: !!deferredPrompt,
    isIos,
    isAndroid,
    isStandalone,
    install,
  }
}
